import { useState, useMemo } from 'react';
import { useTasks } from '../../../contexts/tasks/TasksContext';
import { useTaskManagement } from '../../tasks/hooks/useTaskManagement';
import { notify } from '../../../utils/toast';
import { Task, Lead } from '../../../types';
import { ChatConversation } from './useChat';

export const useContactPanelTasks = (conversation: ChatConversation | undefined, currentLeads: Lead[]) => {
    // Usa o contexto global de tarefas
    const { tasks: allTasks, addTask } = useTasks();

    // Estado local apenas para controlar a abertura da sheet
    const [isCreatingTask, setIsCreatingTask] = useState(false);

    // Filtra as tarefas do lead atual
    const currentTasks = useMemo(() => {
        const firstLead = currentLeads[0];
        if (!firstLead) return [];
        return allTasks.filter(task => task.leadId === firstLead.id);
    }, [currentLeads, allTasks]);

    // Usa o hook centralizado para ações de tarefas (toggle, delete, duplicate, save)
    const {
        toggleTaskCompletion,
        deleteTask,
        duplicateTask,
        saveTask
    } = useTaskManagement(currentTasks);

    // Handler específico para criação via formulário rápido do chat
    const handleCreateTask = async (taskData: Partial<Task>) => {
        if (!conversation) return;

        const leadToUse = currentLeads[0];

        if (!leadToUse) {
            notify.info('Crie uma negociação primeiro para poder adicionar tarefas.');
            setIsCreatingTask(false);
            return;
        }

        if (!taskData.title || taskData.title.trim().length < 3) {
            notify.warning('O título da tarefa deve ter pelo menos 3 caracteres.');
            return;
        }

        const newTask: Omit<Task, 'id'> = {
            title: taskData.title.trim(),
            description: '',
            leadId: taskData.leadId || leadToUse?.id,
            dueDate: taskData.dueDate,
            isCompleted: taskData.isCompleted || false,
            // Preferência: payload do form → responsável da negociação
            assignedTo: taskData.assignedTo
                || (leadToUse?.agent ? { id: leadToUse.agent, name: '' } : undefined),
        };

        try {
            await addTask(newTask);
            notify.success('Tarefa criada com sucesso!');
            setIsCreatingTask(false);
        } catch (error: any) {
            console.error('[handleCreateTask] Error:', error);
            notify.error(error.message || 'Erro ao criar tarefa.');
        }
    };

    // Wrappers para manter compatibilidade com a interface existente
    const handleToggleTaskCompletion = async (taskId: string) => {
        const task = currentTasks.find(t => t.id === taskId);
        if (task) {
            await toggleTaskCompletion(task);
        }
    };

    const handleUpdateTask = async (updatedTask: Task) => {
        await saveTask(updatedTask, updatedTask.id);
    };

    const handleDeleteTask = async (taskId: string) => {
        await deleteTask(taskId);
    };

    const handleDuplicateTask = async (task: Task) => {
        await duplicateTask(task);
    };

    return {
        currentTasks,
        isCreatingTask,
        setIsCreatingTask,
        handleCreateTask,
        handleToggleTaskCompletion,
        handleUpdateTask,
        handleDeleteTask,
        handleDuplicateTask
    };
};
